import { Alert, Stack } from "@mui/material";
import { useMemo, useState } from "react";

import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import VehicleDamageViewModal from "@/common/components/Entity/VehicleDamage/View/VehicleDamageViewModal";
import { VehicleVisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { SvgHelper } from "@/common/helpers/svg";
import { Mapper } from "@/common/mapping/mapper";
import { VehicleDamageDtoAll, VehicleDamageDtoAny } from "@/common/mapping/mappers/vehicleDamage";
import { VehicleArea, VehicleProjection } from "@/core/api/generated/v1";

import { VisualModelSvgViewer } from "../VisualModelSvgViewer";

interface Props {
  entity: VehicleDamageDtoAny;
  isDisplayOldVisualModel?: boolean;
}

export function VisualModelViewerOfVehicleDamage({
  entity,
  isDisplayOldVisualModel = false,
}: Props) {
  const entity2 = entity as VehicleDamageDtoAll;

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const visualModelId = entity2.visualModelId || entity2.visualModel?.id;
  const oldVisualModelId = entity2.oldVisualModel?.id;
  const visualModelIdComputed = isDisplayOldVisualModel ? oldVisualModelId : visualModelId;

  const point = useMemo(
    () =>
      isDisplayOldVisualModel
        ? Mapper.visualModel.mapVehicleDamageDtoToVisualModelPoint(entity2, {
            coords:
              SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoordsOrNull(entity2.oldPoint) ||
              undefined,
          })
        : Mapper.visualModel.mapVehicleDamageDtoToVisualModelPoint(entity2),
    [entity2, isDisplayOldVisualModel],
  );

  if (!visualModelIdComputed) {
    return (
      <NoDataAlert
        title='This damage has no visual modal.'
        description="It's likely it was added without placing a point."
      />
    );
  }

  return (
    <Stack gap={1}>
      {(!point || !entity2.point || !entity2.area || !entity2.projection) && (
        <Alert severity='warning'>{`This damage wasn't marked on the visual modal.`}</Alert>
      )}

      <VisualModelSvgViewer
        visualModelId={visualModelIdComputed}
        visualModel={undefined}
        imageSelector={{
          area: entity2.point ? entity2.area || VehicleArea.Exterior : VehicleArea.Exterior,
          projection: entity2.point
            ? entity2.projection || VehicleProjection.Front
            : VehicleProjection.Front,
        }}
        points={point ? [point] : []}
        svgViewerProps={{
          defaultPointStyle: VehicleVisualModelHelper.getSvgPointStyleForVehicleDamage(entity2),
          isZoomEnabled: true,
          isResizeEnabled: true,
          isPointDragEnabled: false,
        }}
        withVisualModelInfo
        onPointClicked={() => {
          setIsViewModalOpen(true);
        }}
      />

      {/* Vehicle damage view modal */}
      <VehicleDamageViewModal
        open={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
        }}
        viewProps={{
          vehicleId: entity2.vehicle?.id,
          vehicleDamageId: entity2.id,
          vehicleDamage: entity2,
          onDelete: null,
        }}
      />
    </Stack>
  );
}
